// src/pages/Donate.jsx
import React, { useState } from 'react';
import {
    Container,
    Paper,
    Box,
    Typography,
    TextField,
    Alert,
    Button,
    Stack,
    Divider,
    CircularProgress,
} from '@mui/material';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';

export default function Donate() {
    const [amount, setAmount] = useState('25.00');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';
    const PAYPAL_AVAILABLE = Boolean(PAYPAL_CLIENT_ID);

    // Read script status from the top-level <PayPalScriptProvider> in App.js
    const [{ isPending, isRejected }] = usePayPalScriptReducer();

    const validateAmount = () => {
        const val = Number.parseFloat(String(amount).replace(/[^0-9.]/g, ''));
        if (!Number.isFinite(val) || val < 1) {
            setError('Please enter an amount of at least $1.00.');
            return null;
        }
        const fixed = val.toFixed(2);
        setAmount(fixed);
        setError('');
        return fixed;
    };

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 1 }}>
                Support the Museum
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
                Your gift helps preserve local history, care for collections, and create educational programs.
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                    alignItems: 'start',
                }}
            >
                {/* LEFT: Artifact Gifts + Mail-a-Check */}
                <Stack spacing={3}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Artifact Gifts
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            In addition to monetary gifts, we gratefully accept historic photographs, documents,
                            uniforms, tools, yearbooks, and other items with Piedmont stories. Visit during open hours
                            or <a href="/contact">contact us</a> to discuss a donation.
                        </Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Prefer to Mail a Check?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            If you’d rather not use PayPal, you can make a donation by mail. Please make checks payable to:
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Piedmont Historical Society Museum</strong><br />
                            <strong>130 N Center Ave, Piedmont, Alabama 36272</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            We’ll gladly send a receipt for your records.
                        </Typography>
                    </Paper>
                </Stack>

                {/* RIGHT: PayPal donation card */}
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                    <Stack spacing={2}>
                        {successMsg && (
                            <Alert severity="success">
                                {successMsg} Thank you! Your generosity helps support our museum and the Historical Society!
                            </Alert>
                        )}
                        {error && <Alert severity="error">{error}</Alert>}

                        <TextField
                            label="Donation Amount (USD)"
                            type="number"
                            inputProps={{ min: 1, step: '0.01', inputMode: 'decimal' }}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            onBlur={validateAmount}
                            helperText="Enter any amount (minimum $1.00)"
                            fullWidth
                        />

                        <TextField
                            label="Optional Note"
                            multiline
                            minRows={2}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Tell us if this gift honors someone or supports a specific exhibit."
                            fullWidth
                        />

                        <Divider />

                        {!PAYPAL_AVAILABLE ? (
                            <Alert severity="warning">
                                PayPal is not available in this build (missing <code>REACT_APP_PAYPAL_CLIENT_ID</code>). Add your PayPal client ID and rebuild.
                            </Alert>
                        ) : isRejected ? (
                            <Alert severity="error">PayPal failed to load. Please refresh the page or try again in a moment.</Alert>
                        ) : (
                            <Box sx={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {isPending ? (
                                    <Stack alignItems="center" spacing={1}>
                                        <CircularProgress />
                                        <Typography variant="caption" color="text.secondary">
                                            Loading secure payment…
                                        </Typography>
                                    </Stack>
                                ) : (
                                    <PayPalButtons
                                        key={`donate-${PAYPAL_CLIENT_ID}`}
                                        forceReRender={[amount]}
                                        style={{
                                            layout: 'vertical',
                                            height: 50,     // ← bigger buttons
                                            label: 'paypal',
                                            color: 'gold',
                                            shape: 'rect',
                                            tagline: false,
                                        }}
                                        disabled={busy}
                                        onClick={(data, actions) => {
                                            const fixed = validateAmount();
                                            if (!fixed) return actions.reject();
                                            setSuccessMsg('');
                                            return actions.resolve();
                                        }}
                                        createOrder={(data, actions) => {
                                            setBusy(true);
                                            const val = Number.parseFloat(amount).toFixed(2);
                                            return actions.order.create({
                                                purchase_units: [
                                                    {
                                                        description: 'Piedmont Historical Society – Donation',
                                                        amount: { currency_code: 'USD', value: val },
                                                    },
                                                ],
                                                application_context: { shipping_preference: 'NO_SHIPPING' },
                                            });
                                        }}
                                        onApprove={async (data, actions) => {
                                            try {
                                                const details = await actions.order.capture();
                                                const orderId = details?.id ? ` (Receipt: ${details.id})` : '';
                                                setSuccessMsg(`Payment completed${orderId}.`);
                                                setError('');
                                            } catch {
                                                setError('There was a problem capturing the payment. Please try again.');
                                            } finally {
                                                setBusy(false);
                                            }
                                        }}
                                        onCancel={() => setBusy(false)}
                                        onError={(err) => {
                                            console.error('PayPal error', err);
                                            setError('PayPal failed to initialize or was blocked. Please try again.');
                                            setBusy(false);
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Stack>
                </Paper>
            </Box>
        </Container>
    );
}
